import React, { useState, useEffect } from 'react';
import { Typography, Select, MenuItem, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { shipments } from '../../content/shipments';
import StandardDataGrid from '../../components/tables/standard_table';
import LineChartBasic from '../../components/charts/LineChartBasic';
import PieChartBasic from '../../components/charts/PieChartBasic';
import LineChartBiaxial from '../../components/charts/LineChartBiaxial';
import BarChartBasic from '../../components/charts/BarChartBasic';
import HorizontalTabs from '../Dashboard/components/HorizontalTabs'
import { useRoleCheckDashboard } from '../../auth/authUtils';

const TestPageDev1 = () => {
    useRoleCheckDashboard("ShippingReport");

    const [selectedDeparturePort, setSelectedDeparturePort] = useState('');
    const [selectedDestination, setSelectedDestination] = useState('');
    const [selectedPort, setSelectedPort] = useState('');
    const [filteredShipments, setFilteredShipments] = useState([]);

    useEffect(() => {
        setSelectedDestination('');
        setSelectedPort('');
        // setFilteredShipments([]);
    }, [selectedDeparturePort]);

    useEffect(() => {
        setSelectedPort('');
        // setFilteredShipments([]);
    }, [selectedDestination]);

    const handleDeparturePortChange = (event) => {
        setSelectedDeparturePort(event.target.value);
    };

    const handleDestinationChange = (event) => {
        setSelectedDestination(event.target.value);
        setFilteredShipments([]);
        if (event.target.value) {
            const filtered = shipments
                .find(ship => ship.departurePort === selectedDeparturePort)
                ?.destinations
                .find(dest => dest.destination === event.target.value)
                ?.destinationPorts.flatMap(port => port.shipments) || [];
            const shipmentsWithId = filtered.map((row, index) => ({ ...row, id: index }));
            setFilteredShipments(shipmentsWithId);
        } else {
            setFilteredShipments([]);
        }
    };

    const handlePortChange = (event) => {
        setSelectedPort(event.target.value);
        setFilteredShipments([]);
        if (selectedDestination && event.target.value) {
            // If both destination and port are selected, filter shipments accordingly
            const filtered = shipments
                .find(ship => ship.departurePort === selectedDeparturePort)
                ?.destinations.find(dest => dest.destination === selectedDestination)
                ?.destinationPorts.find(p => p.port === event.target.value)?.shipments || [];
            const shipmentsWithId = filtered.map((row, index) => ({ ...row, id: index }));
            setFilteredShipments(shipmentsWithId);
        } else {
            setFilteredShipments([]);
        }
    };

    // Function to compute aggregated data for line chart
    const computeAggregatedData = (shipments) => {
        const aggregatedData = {};
        const monthAbbreviations = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        shipments.forEach(shipment => {
            const month = parseInt(shipment.actualETA.substring(3, 5), 10) - 1;
            const formattedMonth = monthAbbreviations[month];

            aggregatedData[formattedMonth] = (aggregatedData[formattedMonth] || 0) + shipment.arrivalActuals;
        });

        const sortedMonths = monthAbbreviations.filter(month => aggregatedData.hasOwnProperty(month));
        const seriesData = sortedMonths.map(month => aggregatedData[month]);
        //seriesData: [{ data: seriesData }] 
        return { xAxis: sortedMonths, seriesData };
    };

    const computeAggregatedepartureData = (shipments) => {
        const aggregatedData = {};
        const monthAbbreviations = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        shipments.forEach(shipment => {
            const month = parseInt(shipment.latestETS.substring(3, 5), 10) - 1;
            const formattedMonth = monthAbbreviations[month];

            aggregatedData[formattedMonth] = (aggregatedData[formattedMonth] || 0) + shipment.actualVolumeShipped;
        });

        const sortedMonths = monthAbbreviations.filter(month => aggregatedData.hasOwnProperty(month));
        const seriesData = sortedMonths.map(month => aggregatedData[month]);
        //seriesData: [{ data: seriesData }] 
        return { xAxis: sortedMonths, seriesData };
    };

    const computeAggregatedPlanData = (shipments) => {
        const aggregatedData = {};
        const monthAbbreviations = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        shipments.forEach(shipment => {
            const month = parseInt(shipment.initialETS.substring(3, 5), 10) - 1;
            const formattedMonth = monthAbbreviations[month];

            aggregatedData[formattedMonth] = (aggregatedData[formattedMonth] || 0) + shipment.plannedVolume;
        });

        const sortedMonths = monthAbbreviations.filter(month => aggregatedData.hasOwnProperty(month));
        const seriesData = sortedMonths.map(month => aggregatedData[month]);
        //seriesData: [{ data: seriesData }] 
        return { xAxis: sortedMonths, seriesData };
    };

    function calculateArrivalTotalsPerDeparturePort(selectedDeparturePort) {
        const result = [];
        const destinationTotals = {};

        shipments.forEach(shipment => {
            if (shipment.departurePort === selectedDeparturePort) {
                shipment.destinations.forEach(destination => {
                    const destinationName = destination.destination;
                    if (!destinationTotals[destinationName]) {
                        destinationTotals[destinationName] = 0;
                    }
                    destination.destinationPorts.forEach(destinationPort => {
                        destinationPort.shipments.forEach(ship => {
                            destinationTotals[destinationName] += ship.arrivalActuals;
                        });
                    });
                });
            }
        });

        let id = 0;
        for (const [destination, totalArrivalActuals] of Object.entries(destinationTotals)) {
            result.push({
                id: id++,
                value: totalArrivalActuals,
                label: `${destination}: ${totalArrivalActuals}`
            });
        }

        return result;
    }

    const calculateArrivalTotalsPerPort = (selectedDeparturePort, selectedDestination) => {
        const destinationShipments = shipments
            .find(ship => ship.departurePort === selectedDeparturePort)
            ?.destinations
            .find(dest => dest.destination === selectedDestination);

        if (!destinationShipments) return []; // If destination not found, return empty array

        const arrivalTotalsPerPort = destinationShipments.destinationPorts.map((port, index) => {
            const totalArrivalActuals = port.shipments.reduce((total, shipment) => {
                return total + (shipment.arrivalActuals || 0);
            }, 0);
            return {
                id: index,
                value: totalArrivalActuals,
                label: `${port.port}: ${totalArrivalActuals}`
            };
        });

        return arrivalTotalsPerPort;
    };

    const calculateSumOfShipmentsPerMonth = (selectedDeparturePort, selectedDestination) => {
        const filteredShipments = shipments
            .find(ship => ship.departurePort === selectedDeparturePort)
            ?.destinations
            .find(dest => dest.destination === selectedDestination)
            ?.destinationPorts.flatMap(port => port.shipments) || [];

        const sumOfShipmentsPerMonth = {};
        filteredShipments.forEach(shipment => {
            const month = shipment.actualETA.substring(3, 5);
            sumOfShipmentsPerMonth[month] = (sumOfShipmentsPerMonth[month] || 0) + 1;
        });

        return sumOfShipmentsPerMonth;
    };

    const calculateSumOfShipmentsDeparturePerMonth = (selectedDeparturePort, selectedDestination) => {
        const filteredShipments = shipments
            .find(ship => ship.departurePort === selectedDeparturePort)
            ?.destinations
            .find(dest => dest.destination === selectedDestination)
            ?.destinationPorts.flatMap(port => port.shipments) || [];

        const sumOfShipmentsPerMonth = {};
        filteredShipments.forEach(shipment => {
            const month = shipment.latestETS.substring(3, 5);
            sumOfShipmentsPerMonth[month] = (sumOfShipmentsPerMonth[month] || 0) + 1;
        });

        return sumOfShipmentsPerMonth;
    };

    const combineData = (aggregatedData, sumOfShipmentsPerMonth) => {
        const sortedKeys = Object.keys(sumOfShipmentsPerMonth).sort(); // Sort keys of sumOfShipmentsPerMonth
        const combinedData = {
            seriesData1: aggregatedData.seriesData,
            xAxis: aggregatedData.xAxis,
            seriesData2: sortedKeys.map(month => sumOfShipmentsPerMonth[month]), // Map sorted keys to corresponding values
            label1: 'Cars',
            label2: 'Ships'
        };

        return combinedData;
    };

    const createTableData = (aggregatedData, aggregatedDepartureData, aggregatedPlanData) => {
        const months = aggregatedData.xAxis;
        const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4'];
        const quarterIndices = [
            [0, 1, 2],  // Q1 indices (Jan, Feb, Mar)
            [3, 4, 5],  // Q2 indices (Apr, May, Jun)
            [6, 7, 8],  // Q3 indices (Jul, Aug, Sep)
            [9, 10, 11] // Q4 indices (Oct, Nov, Dec)
        ];

        let columns = [{ field: 'id', headerName: 'ID' }];
        let monthWithQuarterColumns = ['id'];

        // Add month columns and quarterly columns in sequence
        months.forEach((month, idx) => {
            columns.push({ field: month, headerName: month });
            monthWithQuarterColumns.push(month);

            const quarterIndex = quarterIndices.findIndex(q => q.includes(idx));
            const isLastMonthOfQuarter = quarterIndices[quarterIndex]?.[2] === idx;

            if (isLastMonthOfQuarter) {
                columns.push({ field: quarterLabels[quarterIndex], headerName: quarterLabels[quarterIndex] });
                monthWithQuarterColumns.push(quarterLabels[quarterIndex]);
            }
        });

        const calculateQuarterSum = (data, indices) => {
            return indices.reduce((sum, idx) => sum + (data[idx] || 0), 0);
        };

        const createRow = (data, idLabel) => {
            const row = { id: idLabel };

            monthWithQuarterColumns.forEach((monthOrQuarter, idx) => {
                if (idx === 0) {
                    row[monthOrQuarter] = idLabel;
                } else {
                    const monthIndex = months.indexOf(monthOrQuarter);
                    if (monthIndex !== -1) {
                        row[monthOrQuarter] = data.seriesData[monthIndex] || 0;
                    } else {
                        const quarterIndex = quarterLabels.indexOf(monthOrQuarter);
                        if (quarterIndex !== -1) {
                            row[monthOrQuarter] = calculateQuarterSum(data.seriesData, quarterIndices[quarterIndex]);
                        }
                    }
                }
            });

            return row;
        };

        const row1 = createRow(aggregatedPlanData, 'Plan');
        const row2 = createRow(aggregatedDepartureData, 'Departure');
        const row3 = createRow(aggregatedData, 'Arrival');

        const tableData = {
            columns: columns,
            rows: [row1, row2, row3]
        };

        return tableData;
    };

    const transformDataToBarChart = (data) => {
        const months = data.columns
            .filter(column => column.field !== 'id' && !['Q1', 'Q2', 'Q3', 'Q4'].includes(column.field))
            .map(column => column.field);

        const dataset = months.map(month => {
            const entry = { month: month };
            data.rows.forEach(row => {
                entry[row.id.toLowerCase()] = row[month];
            });
            return entry;
        });

        const valueFormatter = (value) => `${value} cars`;

        const xAxis = [{ scaleType: 'band', dataKey: 'month' }];

        const series = [
            { dataKey: 'plan', label: 'Plan', valueFormatter },
            { dataKey: 'departure', label: 'Departure', valueFormatter },
            { dataKey: 'arrival', label: 'Arrival', valueFormatter }
        ];

        return { dataset, xAxis, series };
    };

    const filteredPorts = shipments.find(ship => ship.departurePort === selectedDeparturePort)?.destinations || [];
    const aggregatedData = computeAggregatedData(filteredShipments);
    const arrivalTotalsPerPort = calculateArrivalTotalsPerPort(selectedDeparturePort, selectedDestination);
    const sumOfShipmentsPerMonth = calculateSumOfShipmentsPerMonth(selectedDeparturePort, selectedDestination);
    const aggregatedDepartureData = computeAggregatedepartureData(filteredShipments);
    const aggregatedPlanData = computeAggregatedPlanData(filteredShipments);
    const sumOfShipmentsDeparturePerMonth = calculateSumOfShipmentsDeparturePerMonth(selectedDeparturePort, selectedDestination);
    const combinedData = combineData(aggregatedData, sumOfShipmentsPerMonth);
    const combinedDepartureData = combineData(aggregatedDepartureData, sumOfShipmentsDeparturePerMonth);
    const tableData = createTableData(aggregatedData, aggregatedDepartureData, aggregatedPlanData);
    const arrivalTotalsPerDeparturePort = calculateArrivalTotalsPerDeparturePort(selectedDeparturePort);
    const barChartData = transformDataToBarChart(tableData);
    //console.log(aggregatedData);
    console.log(arrivalTotalsPerPort);

    const columns = [
        { field: 'id', headerName: 'ID' },
        { field: 'vessel', headerName: 'Vessel', width: 150 },
        { field: 'initialETS', headerName: 'Initial ETS', width: 130 },
        { field: 'latestETS', headerName: 'Latest ETS', width: 130 },
        { field: 'departureDelay', headerName: 'Departure Delay', width: 150 },
        { field: 'plannedVolume', headerName: 'Planned Volume', width: 150 },
        { field: 'adjustedVolume', headerName: 'Adjusted Volume', width: 150 },
        { field: 'stock', headerName: 'Stock', width: 120 },
        { field: 'shippingCapacityBottleneck', headerName: 'Shipping Capacity Bottleneck', width: 250 },
        { field: 'actualVolumeShipped', headerName: 'Actual Volume Shipped', width: 200 },
        { field: 'initialETA', headerName: 'Initial ETA', width: 130 },
        { field: 'actualETA', headerName: 'Actual ETA', width: 130 },
        { field: 'arrivalDelay', headerName: 'Arrival Delay', width: 130 },
        { field: 'arrivalActuals', headerName: 'Actual Arrivals', width: 150 },
    ];

    return (
        <Box sx={{ p: 5 }}>
            <Typography variant="h2">Shipping Report</Typography>
            <Grid container spacing={2} marginBottom={2} marginTop={2}>
                <Grid item xs={12} md={4}>
                    <Select
                        value={selectedDeparturePort}
                        onChange={handleDeparturePortChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value="" disabled>
                            Select Departure Port
                        </MenuItem>
                        {shipments.map(ship => (
                            <MenuItem key={ship.departurePort} value={ship.departurePort}>
                                {ship.departurePort}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Select
                        value={selectedDestination}
                        onChange={handleDestinationChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                        disabled={!selectedDeparturePort}
                    >
                        <MenuItem value="" disabled>
                            Select Destination
                        </MenuItem>
                        {filteredPorts.map(dest => (
                            <MenuItem key={dest.destination} value={dest.destination}>
                                {dest.destination}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Select
                        value={selectedPort}
                        onChange={handlePortChange}
                        variant="outlined"
                        displayEmpty
                        fullWidth
                        disabled={!selectedDestination}
                    >
                        <MenuItem value="" disabled>
                            Select Port
                        </MenuItem>
                        {selectedDestination &&
                            shipments.find(ship => ship.departurePort === selectedDeparturePort)?.destinations.find(dest => dest.destination === selectedDestination)?.destinationPorts.map(port => (
                                <MenuItem key={port.port} value={port.port}>
                                    {port.port}
                                </MenuItem>
                            ))}
                    </Select>
                </Grid>
            </Grid>
            {selectedDeparturePort && !selectedDestination && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h3" marginTop={5}>
                                Car Arrivals per Country from {selectedDeparturePort}
                            </Typography>
                            <PieChartBasic
                                data={arrivalTotalsPerDeparturePort}
                                height={400}
                            />
                        </Grid>
                    </Grid>
                </>)}
            {selectedDestination && !selectedPort && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <HorizontalTabs
                                combinedData={combinedData}
                                combinedDepartureData={combinedDepartureData}
                                barChartData={barChartData}
                            />
                        </Grid>
                        {/* <Grid item xs={12} md={6}>
                            <Typography variant="h3" marginTop={5}>
                                Car and Ship Departures per Month in {selectedDestination}
                            </Typography>
                            <LineChartBiaxial
                                data={combinedDepartureData}
                                height={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h3" marginTop={5}>
                                Car and Ship Arrivals per Month in {selectedDestination}
                            </Typography>
                            <LineChartBiaxial
                                data={combinedData}
                                height={400}
                            />
                        </Grid> */}
                        <Grid item xs={12} md={6}>
                            <Typography variant='h3' marginTop={5} marginBottom={5}>
                                Total numbers of Cars planed, departed and arrived per Month in {selectedDestination}
                            </Typography>
                            <Box>
                                <StandardDataGrid rows={tableData.rows} columns={tableData.columns} columnIdVisibiliy={true} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h3" marginTop={5}>
                                Car Arrivals per Port in {selectedDestination}
                            </Typography>
                            <PieChartBasic
                                data={arrivalTotalsPerPort}
                                height={400}
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <BarChartBasic
                                dataset={barChartData.dataset}
                                series={barChartData.series}
                                xAxis={barChartData.xAxis}
                                height={400}
                            />
                        </Grid> */}
                    </Grid>
                </>
            )}
            {selectedPort && (
                <>
                    <Box sx={{ height: '60vh', width: '100%' }}>
                        <StandardDataGrid rows={filteredShipments} columns={columns} columnIdVisibiliy={false} />
                    </Box>
                    <Typography variant="h3" marginTop={5}>
                        Car Arrivals per Month in {selectedDestination} on Port {selectedPort}
                    </Typography>
                    <LineChartBasic
                        data={aggregatedData}
                        height={400}
                    />
                </>
            )}
        </Box>
    );
};

export { TestPageDev1 };
